import {
    createSlice, createAsyncThunk, createSelector, PayloadAction
} from '@reduxjs/toolkit';
import type {Dispatch, ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';

import {userApi} from '@api/user/api';
import {groupApi} from '@api/group/api';
import {featureApi} from '@api/feature/api';
import {accountApi} from '@api/account/api';
import {brandApi} from '@api/brand/api';
import {transactionApi} from '@api/transaction/api';
import {documentApi} from '@api/document/api';
import {fieldApi} from '@api/field/api';
import {approvalApi} from '@api/approval/api';
import {signingApi} from '@api/signing/api';
import {recipientApi} from '@api/recipient/api';
import {attachmentApi} from '@api/attachment/api';
import {uploadAttachment as uploadAttachmentApi, getAllAttachmentUploads, deleteAttachmentUpload as deleteAttachmentUploadApi} from '@api/attachmentUpload/api';
import {configurationApi} from '@api/configuration/api';
import {documentVisibilityApi} from '@api/documentVisibility/api';
import {esvApi} from '@api/esv/api';
import {conditionalFieldApi} from '@api/conditionalField/api';

import type {ApprovalView} from '@api/approval/api.d';
import type {FieldView} from '@api/field/api.d';
import type {RecipientView} from '@api/recipient/api.d';
import type {DocumentView} from '@api/types/document';
import type {Account} from '@api/account/api.d';
import type {BrandResource} from '@api/brand/api.d';
import type {Transaction} from '@api/transaction/api.d';
import type {SignatureResource} from '@api/signature/api.d';
import type {AttachmentView} from '@api/attachment/api.d';
import type {AttachmentUploadView} from '@api/attachmentUpload/api.d';
import type {ConfigurationResource, ConfigurationType} from '@api/configuration/api.d';
import type {DocumentVisibilityConfiguration} from '@api/documentVisibility/api.d';
import type {VerificationView, AccountProviderView} from '@api/esv/api.d';

import {parseJWT, getIsDelegationToken, getIsActorToken} from '@utils/jwtUtils';
import {hardRedirectTo} from '@utils/helpers';
import {getErrorDetails} from '@utils/requests/error';

import {AUTH_USER_ROLE, AUTH_RECIPIENT_ROLE} from '@constants/user';
import {DEFAULT_LOCALE} from '@constants';
import {APPROVAL_TYPES, FIELD_TYPES} from '@api/constants';

import {nextPendingUpload} from './utils/selectors';
import {resolveActorUserId} from './utils/resolveActorUserId';
import {getIsConditionSource} from './utils/getIsConditionSource';

import {signatureApi} from '@api/signature/api';
import type {
    SignerUiState,
    RootState,
    Locale,
    AuthObject,
    FieldCondition,
    CachedSignatureData,
    DelegateSignature,
    JWTPayload,
    UserFetchResult,
    ApiErrorPayload,
    FetchApprovalsResult,
    FetchDocumentFieldsResult,
    FetchAttachmentUploadsResult,
    FetchUserArgs,
    FetchTransactionArgs,
    FetchRecipientArgs,
    FetchApprovalsArgs,
    FetchAttachmentsArgs,
    FetchDocumentApprovalsArgs,
    FetchDocumentFieldsArgs,
    FetchAttachmentUploadsArgs,
    FillFieldArgs,
    SignApprovalArgs,
    UpdateRadioGroupSelectionArgs,
    UpdateRadioGroupSelectionResult,
    ConfirmDocumentArgs,
    UndoApprovalArgs,
    UploadAttachmentFileArgs,
    DeleteAttachmentUploadArgs,
    FetchConfigurationArgs,
    FetchConfigurationResult,
    RadioGroupState,
    ChangeRecipientArgs,
    ChangeRecipientError,
    UpdateRecipientLanguageArgs,
    CreateDeclineReasonArgs,
    CreateDeclineReasonResult,
    FetchAllRecipientsArgs,
    NextSigning,
    FetchNextSigningArgs
} from './signerUiSlice.types';

type AppDispatch = ThunkDispatch<RootState, undefined, UnknownAction> & Dispatch;

/** Matches the approval-service page size so pagination stops only when a short page comes back. */
const RECIPIENT_APPROVALS_PAGE_SIZE = 10;

export const fetchUser = createAsyncThunk<
    UserFetchResult | null,
    FetchUserArgs,
    {state: RootState; rejectValue: string}
>(
    'signerUi/fetchUser',
    async ({config, accessToken: passedAccessToken}, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const accessToken = passedAccessToken || state.user?.accessToken;

            if (!accessToken) {
                return null;
            }

            const tokenPayload = parseJWT(accessToken) as JWTPayload | null;
            const role = tokenPayload?.role;

            if (role === AUTH_USER_ROLE) {
                const externalId = tokenPayload?.external_id;
                if (!externalId) {
                    return null;
                }

                const userResponse = await userApi.getUser({
                    userId: externalId,
                    options: {accessToken}
                });

                const result: UserFetchResult = {
                    user: userResponse?.data || null,
                    activeAccountId: userResponse?.data?.session_account_id || null,
                    accessToken
                };

                return result;
            }
            if (role === AUTH_RECIPIENT_ROLE) {
                const accounts = tokenPayload?.accounts;
                let accountsArray: any[] = [];
                if (Array.isArray(accounts)) {
                    accountsArray = accounts;
                } else if (accounts) {
                    accountsArray = [accounts];
                }
                const accountId = accountsArray[0]?.id || null;

                if (getIsActorToken(accessToken)) {
                    const actorUserId = await resolveActorUserId(accessToken);
                    if (actorUserId) {
                        const actorUserResponse = await userApi.getUser({
                            userId: actorUserId,
                            options: {accessToken}
                        });
                        return {
                            user: actorUserResponse?.data || null,
                            activeAccountId: actorUserResponse?.data?.session_account_id || accountId,
                            accessToken
                        };
                    }
                }

                const recipientEmail = tokenPayload?.email_aliases?.[0];

                const defaultResult: UserFetchResult = {
                    user: null,
                    activeAccountId: accountId,
                    accessToken
                };

                if (recipientEmail) {
                    try {
                        const userByEmailResponse = await userApi.getUserByEmail({
                            params: {email: recipientEmail},
                            options: {accessToken}
                        });

                        if (userByEmailResponse?.data) {
                            return {
                                user: userByEmailResponse.data,
                                activeAccountId: userByEmailResponse.data.session_account_id || accountId,
                                accessToken
                            };
                        }
                    } catch {
                        return defaultResult;
                    }
                }

                return defaultResult;
            }

            return null;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchSignature = createAsyncThunk<
    SignatureResource | null,
    {accessToken?: string} | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchSignature',
    async (args, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const user = state.signerUi?.user || (state as any).user?.user;
            const userId = user?.id;
            const passedAccessToken = args && typeof args === 'object' ? args.accessToken : undefined;
            const accessToken = passedAccessToken || state.user?.accessToken;

            const isDelegationToken = !!accessToken && getIsDelegationToken(accessToken);
            const actorUserId = isDelegationToken && accessToken
                ? parseJWT(accessToken)?.exchange_actor_user_id
                : null;
            const signatureUserId = (typeof actorUserId === 'string' && actorUserId) ? actorUserId : userId;

            if (!signatureUserId) {
                return null;
            }

            const signatureResponse = await signatureApi.getSignature(signatureUserId, {accessToken});

            return (signatureResponse?.data as unknown as SignatureResource) || null;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchRecipient = createAsyncThunk<
    RecipientView | null,
    FetchRecipientArgs | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchRecipient',
    async (args, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const passedAccessToken = args && typeof args === 'object' ? args.accessToken : undefined;
            const passedRecipientId = args && typeof args === 'object' ? args.recipientId : undefined;
            const accessToken = passedAccessToken || state.user?.accessToken;
            const {transaction} = state.signerUi;
            const transactionId = transaction?.id;

            if (!accessToken) {
                return null;
            }

            if (!transactionId) {
                return null;
            }

            const tokenPayload = parseJWT(accessToken) as JWTPayload | null;
            const role = tokenPayload?.role;

            let recipientId: string | undefined;
            if (role === AUTH_USER_ROLE) {
                recipientId = passedRecipientId;
            } else if (role === AUTH_RECIPIENT_ROLE) {
                recipientId = tokenPayload?.external_id;
            }

            if (!recipientId) {
                return null;
            }

            const recipientResponse = await recipientApi.getRecipient(
                transactionId,
                recipientId,
                {accessToken}
            );

            const recipient = recipientResponse?.data || null;

            return recipient;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const initializeSigningRecipient = createAsyncThunk<
    RecipientView | null,
    FetchRecipientArgs | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/initializeSigningRecipient',
    async (args, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const passedAccessToken = args && typeof args === 'object' ? args.accessToken : undefined;
            const passedRecipientId = args && typeof args === 'object' ? args.recipientId : undefined;
            const accessToken = passedAccessToken || state.user?.accessToken;
            const {transaction} = state.signerUi;
            const transactionId = transaction?.id;

            if (!accessToken) {
                return null;
            }

            if (!transactionId) {
                return null;
            }

            const tokenPayload = parseJWT(accessToken) as JWTPayload | null;
            const role = tokenPayload?.role;

            let recipientId: string | undefined;
            if (role === AUTH_USER_ROLE) {
                recipientId = passedRecipientId;
            } else if (role === AUTH_RECIPIENT_ROLE) {
                recipientId = tokenPayload?.external_id;
            }

            if (!recipientId) {
                return null;
            }

            const recipientResponse = await recipientApi.initializeSigning(
                transactionId,
                recipientId,
                {accessToken}
            );

            const recipient = recipientResponse?.data || null;

            return recipient;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchAccount = createAsyncThunk<
    Account | null,
    {accessToken?: string} | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchAccount',
    async (args, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const {activeAccountId} = state.signerUi;
            const passedAccessToken = args && typeof args === 'object' ? args.accessToken : undefined;
            const accessToken = passedAccessToken || state.user?.accessToken;

            if (!activeAccountId || !accessToken) {
                return null;
            }

            const accountResponse = await accountApi.getAccount(activeAccountId, {accessToken});
            const account = accountResponse?.data || null;
            return account;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchGroup = createAsyncThunk<
    any | null,
    {accessToken?: string} | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchGroup',
    async (args, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const {recipient} = state.signerUi;
            const passedAccessToken = args && typeof args === 'object' ? args.accessToken : undefined;
            const accessToken = passedAccessToken || state.user?.accessToken;

            const accountId = recipient?.account_id;
            const groupId = recipient?.group_id;
            if (!accountId || !groupId || !accessToken) {
                return null;
            }

            const response = await groupApi.getGroup({
                accountId,
                groupId,
                accessToken
            });
            return response?.data || null;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchDelegateUser = createAsyncThunk<
    any | null,
    {accessToken?: string} | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchDelegateUser',
    async (args, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const passedAccessToken = args && typeof args === 'object' ? args.accessToken : undefined;
            const accessToken = passedAccessToken || state.user?.accessToken;

            if (!accessToken || !getIsDelegationToken(accessToken)) {
                return null;
            }

            const tokenPayload: any = parseJWT(accessToken);
            const delegateUserId = tokenPayload?.exchange_actor_user_id;
            if (typeof delegateUserId !== 'string' || !delegateUserId) {
                return null;
            }

            const response = await userApi.getUser({
                userId: delegateUserId,
                options: {accessToken}
            });
            return response?.data || null;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchDefaultBrand = createAsyncThunk<
    BrandResource | null,
    {accountId?: string; useS2SAuth?: boolean} | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchDefaultBrand',
    async (args, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const passedAccountId = args && typeof args === 'object' ? args.accountId : undefined;
            const useS2SAuth = args && typeof args === 'object' ? args.useS2SAuth : undefined;
            const brandAccountId = passedAccountId;

            if (!brandAccountId) {
                return null;
            }

            if (useS2SAuth) {
                const brandResponse = await brandApi.getDefaultBrand(brandAccountId, {useS2SAuth: true});
                return brandResponse?.data || null;
            }

            const accessToken = state.user?.accessToken;
            if (!accessToken) {
                return null;
            }

            const brandResponse = await brandApi.getDefaultBrand(brandAccountId, {accessToken});
            return brandResponse?.data || null;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchAllDocuments = createAsyncThunk<
    DocumentView[],
    {accessToken?: string} | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchAllDocuments',
    async (args, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const {transaction} = state.signerUi;
            const transactionId = transaction?.id;
            const passedAccessToken = args && typeof args === 'object' ? args.accessToken : undefined;
            const accessToken = passedAccessToken || state.user?.accessToken;

            if (!transactionId) {
                return [];
            }

            const documents = await documentApi.getAllDocuments(transactionId, {}, {accessToken});

            return documents;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchTransaction = createAsyncThunk<
    Transaction | null,
    FetchTransactionArgs,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchTransaction',
    async ({transactionId, accessToken: passedAccessToken}, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const accessToken = passedAccessToken || state.user?.accessToken;

            if (!transactionId) {
                return null;
            }

            const transactionResponse = await transactionApi.getTransactionOrTemplateById({
                transactionId,
                ...(accessToken ? {accessToken} : {})
            });

            const transaction = transactionResponse?.data || null;

            return transaction;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchAllRecipients = createAsyncThunk<
    RecipientView[],
    FetchAllRecipientsArgs | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchAllRecipients',
    async (args, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const passedAccessToken = args && typeof args === 'object' ? args.accessToken : undefined;
            const accessToken = passedAccessToken || state.user?.accessToken;
            const {transaction} = state.signerUi;
            const transactionId = transaction?.id;

            if (!transactionId || !accessToken) {
                return [];
            }

            const recipients = await recipientApi.getAllRecipients(
                transactionId,
                {},
                {accessToken}
            );

            return recipients;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

function getFieldsByApprovalId(
    fields: FieldView[],
    approvalIds: string[],
    currentFields: Record<string, FieldView[]>,
    savingFieldIds: string[]
): Record<string, FieldView[]> {
    const fieldsByApprovalId: Record<string, FieldView[]> = {};
    approvalIds.forEach((approvalId) => {
        fieldsByApprovalId[approvalId] = [];
    });

    fields.forEach((field) => {
        if (!fieldsByApprovalId[field.approval_id]) {
            fieldsByApprovalId[field.approval_id] = [];
        }

        const isSaving = savingFieldIds.includes(field.id);
        const currentField = isSaving
            ? (currentFields[field.approval_id] || []).find((existingField) => existingField.id === field.id)
            : undefined;

        fieldsByApprovalId[field.approval_id].push(currentField || field);
    });

    return fieldsByApprovalId;
}

function getScopedRecipientId(
    accessToken: string,
    passedRecipientId?: string,
    stateRecipientId?: string
): string | undefined {
    const tokenPayload = parseJWT(accessToken) as JWTPayload | null;
    const role = tokenPayload?.role;

    if (role === AUTH_USER_ROLE) {
        return passedRecipientId || stateRecipientId;
    }

    if (role === AUTH_RECIPIENT_ROLE) {
        return tokenPayload?.external_id;
    }

    return undefined;
}

export const fetchDocumentApprovals = createAsyncThunk<
    FetchApprovalsResult,
    FetchDocumentApprovalsArgs,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchDocumentApprovals',
    async (options, {rejectWithValue, getState}) => {
        try {
            const {documentId, accessToken: passedAccessToken, recipientId: passedRecipientId} = options;
            const state = getState();
            const {transaction, recipient} = state.signerUi;
            const transactionId = transaction?.id;
            const accessToken = passedAccessToken || state.user?.accessToken;

            if (!transactionId || !documentId || !accessToken) {
                return {documentId, approvals: []};
            }

            const recipientId = getScopedRecipientId(accessToken, passedRecipientId, recipient?.id);

            if (!recipientId) {
                return {documentId, approvals: []};
            }

            const approvals = await approvalApi.getAllApprovalsByRecipientId(
                transactionId,
                documentId,
                recipientId,
                {per_page: RECIPIENT_APPROVALS_PAGE_SIZE},
                {accessToken}
            );

            return {documentId, approvals: approvals || []};
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchDocumentFields = createAsyncThunk<
    FetchDocumentFieldsResult,
    FetchDocumentFieldsArgs,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchDocumentFields',
    async (options, {rejectWithValue, getState}) => {
        try {
            const {documentId, accessToken: passedAccessToken, recipientId: passedRecipientId} = options;
            const state = getState();
            const {transaction, recipient} = state.signerUi;
            const transactionId = transaction?.id;
            const accessToken = passedAccessToken || state.user?.accessToken;

            if (!transactionId || !documentId || !accessToken) {
                return {documentId, fields: []};
            }

            const recipientId = getScopedRecipientId(accessToken, passedRecipientId, recipient?.id);

            if (!recipientId) {
                return {documentId, fields: []};
            }

            const response = await fieldApi.getFieldsForRecipientInDocument(
                transactionId,
                documentId,
                recipientId,
                {accessToken}
            );

            return {documentId, fields: (response?.data as unknown as FieldView[]) || []};
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fillField = createAsyncThunk<
    FieldView | undefined,
    FillFieldArgs,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fillField',
    async ({
        fieldId, approvalId, value, documentId
    }, {rejectWithValue, getState, dispatch}) => {
        dispatch(setIsApprovalOrFieldSaving({id: fieldId, isSaving: true}));
        try {
            const state = getState();
            const {transaction} = state.signerUi;
            const transactionId = transaction?.id;
            const accessToken = state.user?.accessToken;

            if (!transactionId || !documentId || !approvalId || !fieldId) {
                throw new Error('Missing required parameters for fillField');
            }

            const stringValue = typeof value === 'string' ? value : String(value ?? '');

            const response = await fieldApi.fillField(
                transactionId,
                documentId,
                approvalId,
                fieldId,
                {value: stringValue},
                {accessToken}
            );

            const updatedField = response?.data as unknown as FieldView | undefined;

            const isConditionSource = getIsConditionSource({
                fieldId,
                fields: state.signerUi.fields[approvalId],
                conditions: state.signerUi.fieldsConditions
            });

            if (isConditionSource) {
                const recipientId = state.signerUi.recipient?.id;
                dispatch(fetchDocumentApprovals({documentId, recipientId, accessToken}) as any);
                dispatch(fetchDocumentFields({documentId, recipientId, accessToken}) as any);
            }

            return updatedField;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                messageKey: getErrorDetails(error).messageKey
            });
        } finally {
            dispatch(setIsApprovalOrFieldSaving({id: fieldId, isSaving: false}));
        }
    }
);

export const updateRadioGroupSelection = createAsyncThunk<
    UpdateRadioGroupSelectionResult,
    UpdateRadioGroupSelectionArgs,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/updateRadioGroupSelection',
    async ({
        groupName, selectedFieldId, approvalId, documentId
    }, {rejectWithValue, getState, dispatch}) => {
        try {
            const state = getState();
            const {transaction} = state.signerUi;
            const transactionId = transaction?.id;

            if (!transactionId || !documentId || !approvalId || !selectedFieldId) {
                throw new Error('Missing required parameters for updateRadioGroupSelection');
            }

            await dispatch(fillField({
                fieldId: selectedFieldId,
                approvalId,
                value: 'true',
                documentId
            }));

            return {
                groupName,
                selectedFieldId,
                approvalId
            };
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchAttachments = createAsyncThunk<
    AttachmentView[],
    FetchAttachmentsArgs | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchAttachments',
    async (args, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const {transaction} = state.signerUi;
            const transactionId = transaction?.id;
            const passedAccessToken = args && typeof args === 'object' ? args.accessToken : undefined;
            const passedRecipientId = args && typeof args === 'object' ? args.recipientId : undefined;
            const accessToken = passedAccessToken || state.user?.accessToken;

            if (!accessToken) {
                return [];
            }

            const tokenPayload = parseJWT(accessToken) as JWTPayload | null;
            const role = tokenPayload?.role;

            let recipientId: string | undefined;
            if (role === AUTH_USER_ROLE) {
                recipientId = passedRecipientId;
            } else if (role === AUTH_RECIPIENT_ROLE) {
                recipientId = tokenPayload?.external_id;
            }

            if (!transactionId || !recipientId) {
                return [];
            }

            const attachments = await attachmentApi.getAllAttachmentsByRecipientId(
                transactionId,
                recipientId,
                {},
                {accessToken}
            );

            return attachments || [];
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchAttachmentUploads = createAsyncThunk<
    FetchAttachmentUploadsResult,
    FetchAttachmentUploadsArgs,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchAttachmentUploads',
    async ({attachmentId}, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const {transaction} = state.signerUi;
            const transactionId = transaction?.id;
            const accessToken = state.user?.accessToken;

            if (!transactionId || !attachmentId) {
                return {attachmentId, uploads: []};
            }

            const uploads = await getAllAttachmentUploads(
                transactionId,
                attachmentId,
                {},
                {accessToken}
            );

            return {attachmentId, uploads: uploads || []};
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchConfiguration = createAsyncThunk<
    FetchConfigurationResult,
    FetchConfigurationArgs & {accessToken?: string},
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchConfiguration',
    async ({configurationType, accessToken: passedAccessToken}, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const {activeAccountId} = state.signerUi;
            const accessToken = passedAccessToken || state.user?.accessToken;

            if (!activeAccountId) {
                return {configurationType, configuration: null};
            }

            const response = await configurationApi.getConfiguration(
                activeAccountId,
                configurationType,
                {accessToken}
            );

            return {
                configurationType,
                configuration: response?.data || null
            };
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

type InitialsSource = {first_name?: string | null; last_name?: string | null} | null | undefined;

function getInitialsFromSources(...sources: InitialsSource[]): string {
    for (const source of sources) {
        const firstName = source?.first_name?.trim() || '';
        const lastName = source?.last_name?.trim() || '';
        if (!firstName && !lastName) continue;
        const firstInitial = firstName.charAt(0).toUpperCase();
        const lastInitial = lastName.charAt(0).toUpperCase();
        const initials = firstInitial + lastInitial;
        if (initials) return initials;
    }
    return 'XX';
}

export const signApproval = createAsyncThunk<
    ApprovalView,
    SignApprovalArgs,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/signApproval',
    async ({
        approvalId, documentId, signImage, signedText
    }, {rejectWithValue, getState, dispatch}) => {
        dispatch(setIsApprovalOrFieldSaving({id: approvalId, isSaving: true}));
        try {
            const state = getState();
            const {transaction, recipient} = state.signerUi;
            const transactionId = transaction?.id;

            if (!transactionId || !documentId || !approvalId) {
                const error = 'Missing required parameters for signApproval';
                throw new Error(error);
            }

            const approvals = state.signerUi.approvals[documentId] || [];
            const approval = approvals.find((a: ApprovalView) => a.id === approvalId);

            const signData: Record<string, any> = {};
            if (signImage) {
                signData.signImage = signImage;
            }

            if (signedText) {
                signData.signedText = signedText;
            } else if (approval?.signing_type === APPROVAL_TYPES.INITIALS && !signImage) {
                const delegateUser = state.signerUi?.delegateUser;
                const signerUser = state.signerUi?.user || (state as any).user?.user;
                signData.signedText = getInitialsFromSources(delegateUser, recipient, signerUser);
            }

            const accessToken = state.user?.accessToken;
            const response = await approvalApi.signApproval(
                transactionId,
                documentId,
                approvalId,
                Object.keys(signData).length > 0 ? (signData as any) : undefined,
                {accessToken}
            );

            return response?.data;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        } finally {
            dispatch(setIsApprovalOrFieldSaving({id: approvalId, isSaving: false}));
        }
    }
);

export const confirmDocument = createAsyncThunk<
    any,
    ConfirmDocumentArgs,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/confirmDocument',
    async ({documentId}, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const {transaction, recipient} = state.signerUi;
            const transactionId = transaction?.id;
            const recipientId = recipient?.id;
            const accessToken = state.user?.accessToken;

            if (!transactionId || !documentId || !recipientId) {
                const error = 'Missing required parameters for confirmDocument';
                throw new Error(error);
            }

            const response = await signingApi.confirmDocument({
                transactionId,
                documentId,
                recipientId,
                options: {accessToken}
            });

            return response?.data;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });
        }
    }
);

export const undoApproval = createAsyncThunk<
    ApprovalView,
    UndoApprovalArgs,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/undoApproval',
    async ({approvalId, documentId}, {rejectWithValue, getState, dispatch}) => {
        dispatch(setIsApprovalOrFieldSaving({id: approvalId, isSaving: true}));
        try {
            const state = getState();
            const {transaction} = state.signerUi;
            const transactionId = transaction?.id;
            const accessToken = state.user?.accessToken;

            if (!transactionId || !documentId || !approvalId) {
                const error = 'Missing required parameters for undoApproval';
                throw new Error(error);
            }

            const response = await approvalApi.undoApproval(
                transactionId,
                documentId,
                approvalId,
                {accessToken}
            );

            return response?.data;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        } finally {
            dispatch(setIsApprovalOrFieldSaving({id: approvalId, isSaving: false}));
        }
    }
);

export const uploadAttachmentFile = createAsyncThunk<
    AttachmentUploadView,
    UploadAttachmentFileArgs,
    {state: RootState; rejectValue: ApiErrorPayload; dispatch: AppDispatch}
>(
    'signerUi/uploadAttachmentFile',
    async ({attachmentId, file}, {rejectWithValue, dispatch, getState}) => {
        try {
            const state = getState();
            const transactionId = state.signerUi.transaction?.id;
            const accessToken = state.user?.accessToken;

            if (!transactionId || !attachmentId || !file) {
                throw new Error('Missing required parameters for uploadAttachmentFile');
            }

            const response = await uploadAttachmentApi(transactionId, attachmentId, file, {accessToken});

            await dispatch(fetchAttachmentUploads({attachmentId}));
            await dispatch(fetchAttachments());

            return response?.data as unknown as AttachmentUploadView;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data
            });
        }
    }
);

export const deleteAttachmentUpload = createAsyncThunk<
    void,
    DeleteAttachmentUploadArgs,
    {state: RootState; rejectValue: ApiErrorPayload; dispatch: AppDispatch}
>(
    'signerUi/deleteAttachmentUpload',
    async ({attachmentId, attachmentUploadId}, {rejectWithValue, dispatch, getState}) => {
        try {
            const state = getState();
            const transactionId = state.signerUi.transaction?.id;
            const accessToken = state.user?.accessToken;

            if (!transactionId || !attachmentId || !attachmentUploadId) {
                throw new Error('Missing required parameters for deleteAttachmentUpload');
            }

            await deleteAttachmentUploadApi(transactionId, attachmentId, attachmentUploadId, {accessToken});

            await dispatch(fetchAttachmentUploads({attachmentId}));
            await dispatch(fetchAttachments());
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const changeRecipient = createAsyncThunk<
    RecipientView,
    ChangeRecipientArgs,
    {state: RootState; rejectValue: ChangeRecipientError}
>(
    'signerUi/changeRecipient',
    async (recipientData, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const {transaction, recipient} = state.signerUi;
            const transactionId = transaction?.id;
            const recipientId = recipient?.id;
            const accessToken = state.user?.accessToken;

            if (!transactionId || !recipientId) {
                const error = 'Missing transaction or recipient ID for changeRecipient';
                throw new Error(error);
            }

            if (!recipientData.email) {
                const error = 'Email is required for changeRecipient';
                throw new Error(error);
            }

            const changeResponse = await recipientApi.changeRecipient(
                transactionId,
                recipientId,
                {
                    email: recipientData.email,
                    first_name: recipientData.first_name ?? undefined,
                    last_name: recipientData.last_name ?? undefined,
                    title: recipientData.title,
                    organisation: recipientData.organisation,
                    personal_message: recipientData.personal_message,
                    language: recipientData.language as any,
                    authentication: recipientData.authentication as any
                } as any,
                {accessToken}
            );

            const updatedRecipient = changeResponse?.data as unknown as RecipientView | undefined;

            if (!updatedRecipient) {
                throw new Error('Failed to change recipient');
            }

            return updatedRecipient;
        } catch (error: any) {
            const errorStatus = error.response?.status;
            const errorData = error.response?.data;

            const errorPayload: ChangeRecipientError = {
                message: error.message || 'Unknown error occurred',
                status: errorStatus,
                statusText: error.response?.statusText
            };

            if (errorStatus === 422 && errorData?.errors) {
                errorPayload.validationErrors = errorData.errors;
            }

            return rejectWithValue(errorPayload);
        }
    }
);

export const updateRecipientLanguage = createAsyncThunk<
    RecipientView,
    UpdateRecipientLanguageArgs,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/updateRecipientLanguage',
    async ({language}, {rejectWithValue, getState}) => {
        const state = getState();
        const {transaction, recipient} = state.signerUi;
        const transactionId = transaction?.id;
        const recipientId = recipient?.id;
        const accessToken = state.user?.accessToken;

        if (!transactionId || !recipientId) {
            return rejectWithValue({
                message: 'Missing transaction or recipient ID for updateRecipientLanguage'
            });
        }

        try {
            const response = await recipientApi.updateRecipientLanguage(
                transactionId,
                recipientId,
                {language},
                {accessToken}
            );

            const updatedRecipient = response?.data as unknown as RecipientView | undefined;

            if (!updatedRecipient) {
                return rejectWithValue({
                    message: 'Failed to update recipient language'
                });
            }

            return updatedRecipient;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message || 'Unknown error occurred',
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const createDeclineReason = createAsyncThunk<
    CreateDeclineReasonResult,
    CreateDeclineReasonArgs,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/createDeclineReason',
    async ({documentId, recipientId, reason}, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const {transaction} = state.signerUi;
            const transactionId = transaction?.id;
            const transactionAccountId = transaction?.account_id;
            const accessToken = state.user?.accessToken;

            if (!transactionAccountId || !transactionId || !documentId || !recipientId) {
                throw new Error('Missing required parameters for createDeclineReason');
            }

            const response = await transactionApi.createDeclineReason({
                accountId: transactionAccountId,
                transactionId,
                data: {
                    document_id: documentId,
                    recipient_id: recipientId,
                    reason
                },
                accessToken
            });

            return {
                declineReason: response.data
            };
        } catch (error: any) {
            console.error('Failed to decline transaction:', error);
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchDocumentVisibility = createAsyncThunk<
    DocumentVisibilityConfiguration[],
    {accessToken?: string} | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchDocumentVisibility',
    async (args, {rejectWithValue, getState}) => {
        try {
            const state = getState();
            const {transaction} = state.signerUi;
            const transactionId = transaction?.id;
            const passedAccessToken = args && typeof args === 'object' ? args.accessToken : undefined;
            const accessToken = passedAccessToken || state.user?.accessToken;

            if (!transactionId) {
                throw new Error('No transaction ID available');
            }

            const response = await documentVisibilityApi.getTransactionVisibility(
                transactionId,
                {accessToken}
            );

            const configurations = Array.isArray(response?.data)
                ? response.data
                : (response?.data?.configurations || []);

            return configurations;
        } catch (error: any) {
            console.error('Failed to fetch document visibility:', error);
            return rejectWithValue({
                message: error.message || 'Failed to fetch document visibility',
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const exitFaceToFaceSigning = createAsyncThunk<
    {accessToken: string; expiresAt: number | null},
    void,
    {state: RootState; rejectValue: ApiErrorPayload; dispatch: AppDispatch}
>(
    'signerUi/exitFaceToFaceSigning',
    async (_, {rejectWithValue, dispatch}) => {
        try {
            const {deauthorizeExchangeSession} = await import('@api/authorizationToken/api');
            const {setAccessToken, setTokenExpiresAt, fetchSessionInfo} = await import('@components/Login/userSlice');
            const {calculateExpiresAt} = await import('@utils/tokenManager');

            const response = await deauthorizeExchangeSession();
            const tokenData = response?.data;

            if (!tokenData?.access_token) {
                throw new Error('No access token received from deauthorization');
            }

            const accessToken = tokenData.access_token;
            const expiresAt = tokenData.expires_in
                ? calculateExpiresAt(Number(tokenData.expires_in))
                : null;

            dispatch(setAccessToken(accessToken));
            if (expiresAt) {
                dispatch(setTokenExpiresAt(expiresAt));
            }

            await dispatch(fetchSessionInfo());

            return {accessToken, expiresAt};
        } catch (error: any) {
            console.error('[exitFaceToFaceSigning] Failed to deauthorize:', error);
            return rejectWithValue({
                message: error.message || 'Failed to exit Face-to-Face signing',
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchVerification = createAsyncThunk<
    VerificationView | null,
    {accessToken?: string} | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchVerification',
    async (args, {getState}) => {
        const state = getState();
        const {transaction, recipient} = state.signerUi;
        const transactionId = transaction?.id;
        const recipientId = recipient?.id;
        const accessToken = args?.accessToken;

        if (!transactionId || !recipientId) {
            return null;
        }

        const response = await esvApi.getVerification({
            transactionId,
            recipientId,
            options: {accessToken}
        });

        return (response?.data as unknown as VerificationView) || null;
    }
);

export const fetchAccountFeatures = createAsyncThunk<
    unknown,
    {accessToken?: string} | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchAccountFeatures',
    async (args, {getState}) => {
        const state = getState();
        const accountId = state.signerUi.activeAccountId;
        const accessToken = args?.accessToken;

        if (!accountId) {
            return null;
        }

        const response = await featureApi.getAccountFeatures(accountId, {accessToken});

        return response?.data ?? null;
    }
);

export const fetchAccountProvider = createAsyncThunk<
    AccountProviderView | null,
    {accessToken?: string} | void,
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchAccountProvider',
    async (args, {getState}) => {
        const state = getState();
        const {transaction, verification} = state.signerUi;
        const transactionAccountId = transaction?.account_id;
        const accountProviderId = verification?.account_provider_id;
        const accessToken = args?.accessToken;

        if (!transactionAccountId || !accountProviderId) {
            return null;
        }

        const response = await esvApi.getAccountProvider({
            accountId: transactionAccountId,
            providerId: accountProviderId,
            options: {accessToken}
        });

        return (response?.data as unknown as AccountProviderView) || null;
    }
);

const initialState: SignerUiState = {
    transactionId: null,
    urlRecipientId: null,
    selectedSigner: null,
    isLeftMenuOpen: true,
    isDocumentsMenuOpen: false,
    isAttachmentsMenuOpen: false,
    selectedAttachmentId: null,
    locale: DEFAULT_LOCALE,
    isDeclinePanelOpen: false,
    isReassignPaneOpen: false,
    isInterfaceHidden: false,
    authObject: {},
    fieldsConditions: [],
    isHardwareTokenDialogOpen: false,
    cachedHanddrawnSignature: null,
    cachedImageSignature: null,
    cachedSignatureData: null,
    delegateSignature: null,
    animatedSignatureIds: [],
    isDelegatedTransaction: false,
    user: null,
    delegateUser: null,
    activeAccountId: null,
    account: null,
    group: null,
    defaultBrand: null,
    recipient: null,
    transaction: null,
    documents: [],
    documentVisibility: [],
    approvals: {},
    fields: {},
    approvalsLoadedForTransactionId: null,
    fieldsLoadedForTransactionId: null,
    savingApprovalsAndFieldsIds: [],
    fieldValidationErrors: {},
    signature: null,
    attachments: [],
    attachmentUploads: {},
    configurations: {},
    isInitialLoadInProgress: false,
    isRecipientLoading: false,
    isUserLoading: false,
    isAccountLoading: false,
    isGroupLoading: false,
    isDefaultBrandLoading: false,
    isTransactionLoading: false,
    isDocumentsLoading: false,
    isDocumentVisibilityLoading: false,
    isApprovalsLoading: false,
    isFieldsLoading: false,
    isSignatureLoading: false,
    isAttachmentsLoading: false,
    isAttachmentUploadsLoading: false,
    isConfirmDocumentLoading: false,
    isUndoApprovalLoading: false,
    isUploadAttachmentLoading: false,
    isConfigurationLoading: false,
    isChangeRecipientLoading: false,
    changeRecipientError: null,
    isLanguageUpdateLoading: false,
    isCreateDeclineReasonLoading: false,
    isAllRecipientsLoading: false,
    isVerificationLoading: false,
    isAccountProviderLoading: false,
    isConditionsLoading: false,
    allRecipients: [],
    allRecipientsTransactionId: null,
    verification: null,
    accountProvider: null,
    accountFeatures: null,
    isAccountFeaturesLoading: false,
    optionalNavigation: false,
    nextSigning: null,
    isNextSigningLoading: false,
    nextSigningError: null
};

export const fetchTransactionConditions = createAsyncThunk<
    FieldCondition[],
    {transactionId: string; accessToken?: string},
    {state: RootState; rejectValue: ApiErrorPayload}
>(
    'signerUi/fetchTransactionConditions',
    async ({transactionId, accessToken}, {rejectWithValue}) => {
        try {
            if (!transactionId) {
                return [];
            }

            const response = await conditionalFieldApi.getTransactionConditions(transactionId, {accessToken});
            const conditions = response?.data?.conditions || [];

            return conditions;
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

export const fetchNextSigning = createAsyncThunk<
    NextSigning | null,
    FetchNextSigningArgs,
    {state: RootState; rejectValue: ApiErrorPayload; dispatch: AppDispatch}
>(
    'signerUi/fetchNextSigning',
    async ({currentTransactionId, accessToken: passedAccessToken}, {rejectWithValue, getState, dispatch}) => {
        try {
            const state = getState();
            let accessToken = passedAccessToken || state.user?.accessToken;
            let accountId = (state.user as any)?.account?.id
                || state.signerUi?.transaction?.account_id;

            if (!accessToken) {
                return null;
            }

            const {getIsMemberToken} = await import('@utils/jwtUtils');
            if (getIsMemberToken(accessToken)) {
                const {deauthorizeExchangeSession} = await import('@api/authorizationToken/api');
                const {setAccessToken, setTokenExpiresAt} = await import('@components/Login/userSlice');
                const {calculateExpiresAt} = await import('@utils/tokenManager');

                const response = await deauthorizeExchangeSession();
                const tokenData = response?.data;
                if (tokenData?.access_token) {
                    accessToken = tokenData.access_token;
                    const expiresAt = tokenData.expires_in
                        ? calculateExpiresAt(Number(tokenData.expires_in))
                        : null;
                    dispatch(setAccessToken(accessToken));
                    if (expiresAt) {
                        dispatch(setTokenExpiresAt(expiresAt));
                    }
                }
            }

            if (!accountId) {
                return null;
            }

            const listResult = await transactionApi.getTransactions({
                accountId,
                params: {
                    per_page: 1,
                    page: 1,
                    awaiting_signatures: 'true',
                    sort: '-updated_at'
                } as any,
                accessToken
            }) as unknown as {data: Transaction[]};

            const nextTransaction = listResult?.data?.[0];
            if (!nextTransaction) {
                return null;
            }

            const transactionId = nextTransaction.id;

            const [documents, recipients, visibilityResponse] = await Promise.all([
                documentApi.getAllDocuments(transactionId, {}, {accessToken}),
                recipientApi.getAllRecipients(transactionId, {}, {accessToken}),
                documentVisibilityApi.getTransactionVisibility(transactionId, {accessToken}).catch(() => null)
            ]);

            const documentVisibility = Array.isArray(visibilityResponse?.data)
                ? visibilityResponse.data
                : (visibilityResponse?.data?.configurations || []);

            const approvalsResults = await Promise.allSettled(
                documents.map((doc) => approvalApi.getAllApprovals(transactionId, doc.id, {}, {accessToken}))
            );

            const approvalsByDocumentId: Record<string, ApprovalView[]> = {};
            const approvalToDocument: Array<{approval: ApprovalView; documentId: string}> = [];
            documents.forEach((doc, idx) => {
                const result = approvalsResults[idx];
                const documentApprovals = result.status === 'fulfilled' ? result.value : [];
                approvalsByDocumentId[doc.id] = documentApprovals;
                documentApprovals.forEach((approval) => {
                    approvalToDocument.push({approval, documentId: doc.id});
                });
            });

            const fieldResults = await Promise.allSettled(
                documents.map((doc) => fieldApi.getFieldsForDocument(transactionId, doc.id, {accessToken}))
            );

            const fieldsByApprovalId: Record<string, FieldView[]> = {};
            approvalToDocument.forEach(({approval}) => {
                fieldsByApprovalId[approval.id] = [];
            });
            fieldResults.forEach((result) => {
                if (result.status !== 'fulfilled') {
                    return;
                }

                ((result.value?.data as unknown as FieldView[]) || []).forEach((field) => {
                    if (fieldsByApprovalId[field.approval_id]) {
                        fieldsByApprovalId[field.approval_id].push(field);
                    }
                });
            });

            return {
                transaction: nextTransaction,
                documents,
                recipients,
                approvalsByDocumentId,
                fieldsByApprovalId,
                documentVisibility
            };
        } catch (error: any) {
            return rejectWithValue({
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText
            });
        }
    }
);

const FIELD_VALIDATION_ERROR_STATUS = 422;
const FIELD_VALIDATION_ERROR_KEY_PREFIX = 'error.validation.';

export const getIsFieldValidationRejection = (payload?: ApiErrorPayload): boolean => (
    payload?.status === FIELD_VALIDATION_ERROR_STATUS
    && Boolean(payload.messageKey?.startsWith(FIELD_VALIDATION_ERROR_KEY_PREFIX))
);

export const signerUiSlice = createSlice({
    name: 'signerUi',
    initialState,
    reducers: {
        setIsApprovalOrFieldSaving: (state, action: PayloadAction<{id: string; isSaving: boolean}>) => {
            const {id, isSaving} = action.payload;
            if (!id) return;

            if (isSaving) {
                if (!state.savingApprovalsAndFieldsIds.includes(id)) {
                    state.savingApprovalsAndFieldsIds.push(id);
                }
            } else {
                state.savingApprovalsAndFieldsIds = state.savingApprovalsAndFieldsIds.filter(
                    (savingItemId) => savingItemId !== id
                );
            }
        },
        setFieldValidationError: (state, action: PayloadAction<{fieldId: string; messageKey: string}>) => {
            const {fieldId, messageKey} = action.payload;
            state.fieldValidationErrors[fieldId] = messageKey;
        },
        clearFieldValidationError: (state, action: PayloadAction<string>) => {
            delete state.fieldValidationErrors[action.payload];
        },
        setApprovalsLoadedForTransaction: (state, action: PayloadAction<string | null>) => {
            state.approvalsLoadedForTransactionId = action.payload;
        },
        setFieldsLoadedForTransaction: (state, action: PayloadAction<string | null>) => {
            state.fieldsLoadedForTransactionId = action.payload;
        },
        setFieldValue: (
            state,
            action: PayloadAction<{fieldId: string; approvalId: string; value: string}>
        ) => {
            const {fieldId, approvalId, value} = action.payload;
            const fieldsForApproval = state.fields[approvalId];
            if (!fieldsForApproval) return;

            const index = fieldsForApproval.findIndex((field) => field.id === fieldId);
            if (index !== -1) {
                fieldsForApproval[index] = {...fieldsForApproval[index], value};
            }
        },
        resetSignerUiState: () => ({
            ...initialState,
            isInitialLoadInProgress: true
        }),
        setIsInitialLoadInProgress: (state, action: PayloadAction<boolean>) => {
            state.isInitialLoadInProgress = action.payload;
        },
        setTransactionId: (state, action: PayloadAction<string | null>) => {
            state.transactionId = action.payload;
        },
        setUrlRecipientId: (state, action: PayloadAction<string | null>) => {
            state.urlRecipientId = action.payload;
        },
        setActiveAccountId: (state, action: PayloadAction<string | null>) => {
            state.activeAccountId = action.payload;
        },
        setSelectedSigner: (state, action: PayloadAction<any>) => {
            state.selectedSigner = action.payload;
        },
        setIsLeftMenuOpen: (state, action: PayloadAction<boolean>) => {
            state.isLeftMenuOpen = action.payload;
            if (!action.payload) {
                state.isDocumentsMenuOpen = false;
                state.isAttachmentsMenuOpen = false;
            }
        },
        setIsDocumentsMenuOpen: (state, action: PayloadAction<boolean>) => {
            state.isDocumentsMenuOpen = action.payload;
        },
        setIsAttachmentsMenuOpen: (state, action: PayloadAction<boolean>) => {
            state.isAttachmentsMenuOpen = action.payload;
        },
        setSelectedAttachmentId: (state, action: PayloadAction<string | null>) => {
            state.selectedAttachmentId = action.payload;
        },
        clearSignature: (state) => {
            state.signature = null;
        },
        setIsDeclinePanelOpen: (state, action: PayloadAction<boolean>) => {
            state.isDeclinePanelOpen = action.payload;
        },
        setIsReassignPaneOpen: (state, action: PayloadAction<boolean>) => {
            state.isReassignPaneOpen = action.payload;
        },
        openNextPendingUpload: (state, action: PayloadAction<any[]>) => {
            state.isAttachmentsMenuOpen = true;
            state.isLeftMenuOpen = true;
            state.isDocumentsMenuOpen = false;
            const attachments = action.payload;
            const selectedAttachmentId = nextPendingUpload(
                attachments,
                state.selectedAttachmentId
            )?.id;
            state.selectedAttachmentId = selectedAttachmentId;
        },
        setAuthObject: (state, action: PayloadAction<AuthObject>) => {
            state.authObject = action.payload;
        },
        setFieldsConditions: (state, action: PayloadAction<FieldCondition[]>) => {
            state.fieldsConditions = action.payload || [];
        },
        setIsHardwareTokenDialogOpen: (state, action: PayloadAction<boolean>) => {
            state.isHardwareTokenDialogOpen = action.payload;
        },
        setIsInterfaceHidden: (state, action: PayloadAction<boolean>) => {
            state.isInterfaceHidden = action.payload;
        },
        setCachedHanddrawnSignature: (state, action: PayloadAction<string | null>) => {
            state.cachedHanddrawnSignature = action.payload;
        },
        setCachedImageSignature: (state, action: PayloadAction<string | null>) => {
            state.cachedImageSignature = action.payload;
        },
        setCachedSignatureData: (state, action: PayloadAction<CachedSignatureData | null>) => {
            state.cachedSignatureData = action.payload;
        },
        clearCachedSignatureData: (state) => {
            state.cachedSignatureData = null;
        },
        setDelegateSignature: (state, action: PayloadAction<DelegateSignature | null>) => {
            state.delegateSignature = action.payload;
        },
        clearDelegateSignature: (state) => {
            state.delegateSignature = null;
        },
        setAnimatedSignatureId: (state, action: PayloadAction<string>) => {
            state.animatedSignatureIds = [...state.animatedSignatureIds, action.payload];
        },
        setIsDelegatedTransaction: (state, action: PayloadAction<boolean>) => {
            state.isDelegatedTransaction = action.payload;
        },
        setDefaultBrand: (state, action: PayloadAction<BrandResource | null>) => {
            state.defaultBrand = action.payload;
        },
        resetNextSigning: (state) => {
            state.nextSigning = null;
            state.isNextSigningLoading = false;
            state.nextSigningError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUser.pending, (state) => {
                state.isUserLoading = true;
            })
            .addCase(fetchUser.fulfilled, (state, action) => {
                state.isUserLoading = false;
                state.user = action.payload?.user || null;
                if (action.payload?.activeAccountId) {
                    state.activeAccountId = action.payload.activeAccountId;
                }
            })
            .addCase(fetchUser.rejected, (state) => {
                state.isUserLoading = false;
            })
            .addCase(fetchRecipient.pending, (state) => {
                state.isRecipientLoading = true;
            })
            .addCase(fetchRecipient.fulfilled, (state, action) => {
                state.isRecipientLoading = false;
                if (action.payload) {
                    state.recipient = action.payload;
                }
            })
            .addCase(fetchRecipient.rejected, (state) => {
                state.isRecipientLoading = false;
            })
            .addCase(initializeSigningRecipient.pending, (state) => {
                state.isRecipientLoading = true;
            })
            .addCase(initializeSigningRecipient.fulfilled, (state, action) => {
                state.isRecipientLoading = false;
                if (action.payload) {
                    state.recipient = action.payload;
                }
            })
            .addCase(initializeSigningRecipient.rejected, (state) => {
                state.isRecipientLoading = false;
            })
            .addCase(fetchAccount.pending, (state) => {
                state.isAccountLoading = true;
            })
            .addCase(fetchAccount.fulfilled, (state, action) => {
                state.isAccountLoading = false;
                if (action.payload) {
                    state.account = action.payload;
                }
            })
            .addCase(fetchAccount.rejected, (state) => {
                state.isAccountLoading = false;
            })
            .addCase(fetchGroup.pending, (state) => {
                state.isGroupLoading = true;
            })
            .addCase(fetchGroup.fulfilled, (state, action) => {
                state.isGroupLoading = false;
                if (action.payload) {
                    state.group = action.payload;
                }
            })
            .addCase(fetchGroup.rejected, (state) => {
                state.isGroupLoading = false;
            })
            .addCase(fetchDelegateUser.fulfilled, (state, action) => {
                state.delegateUser = action.payload || null;
            })
            .addCase(fetchDelegateUser.rejected, (state) => {
                state.delegateUser = null;
            })
            .addCase(fetchDefaultBrand.pending, (state) => {
                state.isDefaultBrandLoading = true;
            })
            .addCase(fetchDefaultBrand.fulfilled, (state, action) => {
                state.isDefaultBrandLoading = false;
                if (action.payload) {
                    state.defaultBrand = action.payload;
                }
            })
            .addCase(fetchDefaultBrand.rejected, (state) => {
                state.isDefaultBrandLoading = false;
            })
            .addCase(fetchTransaction.pending, (state) => {
                state.isTransactionLoading = true;
            })
            .addCase(fetchTransaction.fulfilled, (state, action) => {
                state.isTransactionLoading = false;
                if (action.payload) {
                    state.transaction = action.payload;
                    state.transactionId = action.payload.id;
                    state.optionalNavigation = action.payload.settings?.ceremony?.optional_navigation ?? false;
                }
            })
            .addCase(fetchTransaction.rejected, (state, action) => {
                state.isTransactionLoading = false;

                const errorPayload = action.payload;
                const is404Error = errorPayload?.status === 404;
                const isClientSide = typeof window !== 'undefined';

                if (is404Error && isClientSide) {
                    hardRedirectTo('/not-found/recipient');
                }
            })
            .addCase(fetchAllRecipients.pending, (state, action) => {
                if (!action.meta.arg?.skipSetLoading) {
                    state.isAllRecipientsLoading = true;
                }
            })
            .addCase(fetchAllRecipients.fulfilled, (state, action) => {
                state.isAllRecipientsLoading = false;
                state.allRecipients = action.payload;
                state.allRecipientsTransactionId = state.transaction?.id || null;
            })
            .addCase(fetchAllRecipients.rejected, (state) => {
                state.isAllRecipientsLoading = false;
            })
            .addCase(fetchAllDocuments.pending, (state) => {
                state.isDocumentsLoading = true;
            })
            .addCase(fetchAllDocuments.fulfilled, (state, action) => {
                state.isDocumentsLoading = false;
                state.documents = action.payload;
            })
            .addCase(fetchAllDocuments.rejected, (state) => {
                state.isDocumentsLoading = false;
            })
            .addCase(fetchDocumentVisibility.pending, (state) => {
                state.isDocumentVisibilityLoading = true;
            })
            .addCase(fetchDocumentVisibility.fulfilled, (state, action) => {
                state.isDocumentVisibilityLoading = false;
                state.documentVisibility = action.payload;
            })
            .addCase(fetchDocumentVisibility.rejected, (state) => {
                state.isDocumentVisibilityLoading = false;
            })
            .addCase(fetchDocumentApprovals.pending, (state) => {
                state.isApprovalsLoading = true;
            })
            .addCase(fetchDocumentApprovals.fulfilled, (state, action) => {
                state.isApprovalsLoading = false;
                const {documentId, approvals} = action.payload;
                state.approvals[documentId] = approvals;
            })
            .addCase(fetchDocumentApprovals.rejected, (state) => {
                state.isApprovalsLoading = false;
            })
            .addCase(fetchDocumentFields.pending, (state) => {
                state.isFieldsLoading = true;
            })
            .addCase(fetchDocumentFields.rejected, (state) => {
                state.isFieldsLoading = false;
            })
            .addCase(fetchDocumentFields.fulfilled, (state, action) => {
                state.isFieldsLoading = false;
                const {documentId, fields} = action.payload;
                const documentApprovalIds = (state.approvals[documentId] || []).map((approval) => approval.id);

                state.fields = {
                    ...state.fields,
                    ...getFieldsByApprovalId(
                        fields,
                        documentApprovalIds,
                        state.fields,
                        state.savingApprovalsAndFieldsIds
                    )
                };
            })
            .addCase(fillField.fulfilled, (state, action) => {
                delete state.fieldValidationErrors[action.meta.arg.fieldId];
                // The field value is applied optimistically at send time via
                // `setFieldValue`, in send order. The server response is intentionally
                // NOT written back here: a late (out-of-order) response would otherwise
                // overwrite a newer value the user already typed. Server-side value
                // normalization, if any, is picked up on the next fields fetch.
            })
            .addCase(updateRadioGroupSelection.fulfilled, (state, action) => {
                const {approvalId, groupName, selectedFieldId} = action.payload;
                const fieldsForApproval = state.fields[approvalId];
                if (fieldsForApproval) {
                    state.fields[approvalId] = fieldsForApproval.map((field) => {
                        if (field.group === groupName && field.type?.toLowerCase() === FIELD_TYPES.RADIO) {
                            return {
                                ...field,
                                value: field.id === selectedFieldId ? 'true' : 'false'
                            };
                        }
                        return field;
                    });
                }
            })
            .addCase(fillField.rejected, (state, action) => {
                if (getIsFieldValidationRejection(action.payload) && action.payload?.messageKey) {
                    state.fieldValidationErrors[action.meta.arg.fieldId] = action.payload.messageKey;
                }
            })
            .addCase(signApproval.fulfilled, (state, action) => {
                const updatedApproval = action.payload as ApprovalView | undefined;
                if (updatedApproval) {
                    const documentId = updatedApproval.document_id;
                    const approvalsForDocument = state.approvals[documentId];
                    if (approvalsForDocument) {
                        const index = approvalsForDocument.findIndex(
                            (approval) => approval.id === updatedApproval.id
                        );
                        if (index !== -1) {
                            approvalsForDocument[index] = updatedApproval;
                        }
                    }
                }
            })
            .addCase(signApproval.rejected, () => {
                // Error handling if needed
            })
            .addCase(confirmDocument.pending, (state) => {
                state.isConfirmDocumentLoading = true;
            })
            .addCase(confirmDocument.fulfilled, (state, action) => {
                state.isConfirmDocumentLoading = false;
                const documentId = action.meta.arg.documentId;
                const approvalsForDocument = state.approvals[documentId];
                if (approvalsForDocument) {
                    const confirmedAt = new Date().toISOString();
                    approvalsForDocument.forEach((approval) => {
                        if (!approval.confirmed_at) {
                            approval.confirmed_at = confirmedAt;
                        }
                    });
                }
            })
            .addCase(confirmDocument.rejected, (state) => {
                state.isConfirmDocumentLoading = false;
            })
            .addCase(undoApproval.pending, (state) => {
                state.isUndoApprovalLoading = true;
            })
            .addCase(undoApproval.fulfilled, (state, action) => {
                state.isUndoApprovalLoading = false;
                const updatedApproval = action.payload as ApprovalView | undefined;
                if (updatedApproval) {
                    const documentId = updatedApproval.document_id;
                    const approvalsForDocument = state.approvals[documentId];
                    if (approvalsForDocument) {
                        const index = approvalsForDocument.findIndex(
                            (approval) => approval.id === updatedApproval.id
                        );
                        if (index !== -1) {
                            approvalsForDocument[index] = updatedApproval;
                        }
                    }
                }
            })
            .addCase(undoApproval.rejected, (state) => {
                state.isUndoApprovalLoading = false;
            })
            .addCase(fetchSignature.pending, (state) => {
                state.isSignatureLoading = true;
            })
            .addCase(fetchSignature.fulfilled, (state, action) => {
                state.isSignatureLoading = false;
                if (action.payload) {
                    state.signature = action.payload;
                }
            })
            .addCase(fetchSignature.rejected, (state) => {
                state.isSignatureLoading = false;
            })
            .addCase(fetchAttachments.pending, (state) => {
                state.isAttachmentsLoading = true;
            })
            .addCase(fetchAttachments.fulfilled, (state, action) => {
                state.isAttachmentsLoading = false;
                state.attachments = action.payload;
            })
            .addCase(fetchAttachments.rejected, (state) => {
                state.isAttachmentsLoading = false;
            })
            .addCase(fetchAttachmentUploads.pending, (state) => {
                state.isAttachmentUploadsLoading = true;
            })
            .addCase(fetchAttachmentUploads.fulfilled, (state, action) => {
                state.isAttachmentUploadsLoading = false;
                const {attachmentId, uploads} = action.payload;
                state.attachmentUploads[attachmentId] = uploads;
            })
            .addCase(fetchAttachmentUploads.rejected, (state) => {
                state.isAttachmentUploadsLoading = false;
            })
            .addCase(uploadAttachmentFile.pending, (state) => {
                state.isUploadAttachmentLoading = true;
            })
            .addCase(uploadAttachmentFile.fulfilled, (state) => {
                state.isUploadAttachmentLoading = false;
            })
            .addCase(uploadAttachmentFile.rejected, (state) => {
                state.isUploadAttachmentLoading = false;
            })
            .addCase(fetchConfiguration.pending, (state) => {
                state.isConfigurationLoading = true;
            })
            .addCase(fetchConfiguration.fulfilled, (state, action) => {
                state.isConfigurationLoading = false;
                const {configurationType, configuration} = action.payload;
                state.configurations[configurationType] = configuration as any;
            })
            .addCase(fetchConfiguration.rejected, (state, action) => {
                state.isConfigurationLoading = false;
                const {configurationType} = (action as any).meta.arg;
                state.configurations[configurationType] = null as any;
            })
            .addCase(changeRecipient.pending, (state) => {
                state.isChangeRecipientLoading = true;
                state.changeRecipientError = null;
            })
            .addCase(changeRecipient.fulfilled, (state, action) => {
                state.isChangeRecipientLoading = false;
                state.changeRecipientError = null;
                if (action.payload) {
                    state.recipient = action.payload;
                }
            })
            .addCase(changeRecipient.rejected, (state, action) => {
                state.isChangeRecipientLoading = false;
                state.changeRecipientError = action.payload || null;
            })
            .addCase(updateRecipientLanguage.pending, (state) => {
                state.isLanguageUpdateLoading = true;
            })
            .addCase(updateRecipientLanguage.fulfilled, (state, action) => {
                state.isLanguageUpdateLoading = false;
                if (action.payload) {
                    state.recipient = action.payload;
                }
            })
            .addCase(updateRecipientLanguage.rejected, (state) => {
                state.isLanguageUpdateLoading = false;
            })
            .addCase(createDeclineReason.pending, (state) => {
                state.isCreateDeclineReasonLoading = true;
            })
            .addCase(createDeclineReason.fulfilled, (state) => {
                state.isCreateDeclineReasonLoading = false;
            })
            .addCase(createDeclineReason.rejected, (state) => {
                state.isCreateDeclineReasonLoading = false;
            })
            .addCase(fetchVerification.pending, (state) => {
                state.isVerificationLoading = true;
            })
            .addCase(fetchVerification.fulfilled, (state, action) => {
                state.isVerificationLoading = false;
                state.verification = action.payload;
            })
            .addCase(fetchVerification.rejected, (state) => {
                state.isVerificationLoading = false;
            })
            .addCase(fetchAccountProvider.pending, (state) => {
                state.isAccountProviderLoading = true;
            })
            .addCase(fetchAccountProvider.fulfilled, (state, action) => {
                state.isAccountProviderLoading = false;
                state.accountProvider = action.payload;
            })
            .addCase(fetchAccountProvider.rejected, (state) => {
                state.isAccountProviderLoading = false;
            })
            .addCase(fetchAccountFeatures.pending, (state) => {
                state.isAccountFeaturesLoading = true;
            })
            .addCase(fetchAccountFeatures.fulfilled, (state, action) => {
                state.isAccountFeaturesLoading = false;
                state.accountFeatures = action.payload;
            })
            .addCase(fetchAccountFeatures.rejected, (state) => {
                state.isAccountFeaturesLoading = false;
            })
            .addCase(fetchTransactionConditions.pending, (state) => {
                state.isConditionsLoading = true;
            })
            .addCase(fetchTransactionConditions.fulfilled, (state, action) => {
                state.isConditionsLoading = false;
                state.fieldsConditions = action.payload;
            })
            .addCase(fetchTransactionConditions.rejected, (state) => {
                state.isConditionsLoading = false;
            })
            .addCase(fetchNextSigning.pending, (state) => {
                state.isNextSigningLoading = true;
                state.nextSigningError = null;
            })
            .addCase(fetchNextSigning.fulfilled, (state, action) => {
                state.isNextSigningLoading = false;
                state.nextSigning = action.payload;
            })
            .addCase(fetchNextSigning.rejected, (state, action) => {
                state.isNextSigningLoading = false;
                state.nextSigningError = action.payload?.message || 'Failed to fetch next signing';
                state.nextSigning = null;
            });
    }
});

export const selectTransactionId = (state: RootState): string | null => state.signerUi.transactionId;
export const selectUrlRecipientId = (state: RootState): string | null => state.signerUi.urlRecipientId;
export const selectSelectedSigner = (state: RootState): any => state.signerUi.selectedSigner;
export const selectIsLeftMenuOpen = (state: RootState): boolean => state.signerUi.isLeftMenuOpen;
export const selectIsDocumentsMenuOpen = (state: RootState): boolean => state.signerUi.isDocumentsMenuOpen;
export const selectIsAttachmentsMenuOpen = (state: RootState): boolean => state.signerUi.isAttachmentsMenuOpen;
export const selectSelectedAttachmentId = (state: RootState): string | null => state.signerUi.selectedAttachmentId;
export const selectIsDeclinePanelOpen = (state: RootState): boolean => state.signerUi.isDeclinePanelOpen;
export const selectIsReassignPaneOpen = (state: RootState): boolean => state.signerUi.isReassignPaneOpen;
export const selectFieldsConditions = (state: RootState): FieldCondition[] => state.signerUi.fieldsConditions || [];
export const selectIsConditionsLoading = (state: RootState): boolean => state.signerUi.isConditionsLoading;
export const selectIsHardwareTokenDialogOpen = (state: RootState): boolean => state.signerUi.isHardwareTokenDialogOpen;
export const selectIsInterfaceHidden = (state: RootState): boolean => state.signerUi.isInterfaceHidden;
export const selectOptionalNavigation = (state: RootState): boolean => state.signerUi.optionalNavigation;
export const selectCachedHanddrawnSignature = (state: RootState): string | null => state.signerUi.cachedHanddrawnSignature;
export const selectCachedImageSignature = (state: RootState): string | null => state.signerUi.cachedImageSignature;
export const selectCachedSignatureData = (state: RootState): CachedSignatureData | null => state.signerUi.cachedSignatureData;
export const selectDelegateSignature = (state: RootState): DelegateSignature | null => state.signerUi.delegateSignature;
export const selectIsDelegatedTransaction = (state: RootState): boolean => state.signerUi.isDelegatedTransaction;
export const selectSignerUser = (state: RootState): any | null => state.signerUi.user;
export const selectActiveAccountId = (state: RootState): string | null => state.signerUi.activeAccountId;
export const selectAccount = (state: RootState): Account | null => state.signerUi.account;
export const selectGroup = (state: RootState): any | null => state.signerUi.group;
export const selectIsGroupLoading = (state: RootState): boolean => state.signerUi.isGroupLoading;
export const selectDelegateUser = (state: RootState): any | null => state.signerUi.delegateUser;
export const selectDefaultBrand = (state: RootState): BrandResource | null => state.signerUi.defaultBrand;
export const selectTransaction = (state: RootState): Transaction | null => state.signerUi.transaction;
export const selectDocuments = (state: RootState): DocumentView[] => state.signerUi.documents;
export const selectDocumentVisibility = (state: RootState): DocumentVisibilityConfiguration[] => state.signerUi.documentVisibility;
export const selectVisibleDocuments = createSelector(
    [
        selectDocuments,
        selectDocumentVisibility,
        (state: RootState) => state.signerUi.recipient
    ],
    (documents, visibility, recipient): DocumentView[] => {
        if (!visibility || visibility.length === 0) {
            return documents;
        }

        const recipientId = recipient?.id;
        if (!recipientId) {
            return documents;
        }

        return documents.filter((document) => {
            const visibilityConfig = visibility.find((v) => v.document_id === document.id);

            if (!visibilityConfig) {
                return true;
            }

            const recipientVisibility = visibilityConfig.visibilities?.find(
                (v) => v.recipient_id === recipientId
            );

            if (!recipientVisibility) {
                return false;
            }

            return recipientVisibility.visible;
        });
    }
);
export const selectApprovals = (state: RootState): Record<string, ApprovalView[]> => state.signerUi.approvals ?? {};
export const selectFields = (state: RootState): Record<string, FieldView[]> => state.signerUi.fields;
export const selectFieldsByApprovalId = (approvalId: string) => (state: RootState): FieldView[] => state.signerUi.fields[approvalId] || [];
export const selectApprovalsByDocumentId = (documentId: string | null) => (state: RootState): ApprovalView[] => (documentId ? state.signerUi.approvals[documentId] || [] : []);
export const selectApprovalById = (approvalId: string | null | undefined) => (state: RootState): ApprovalView | null => {
    if (!approvalId) return null;
    const allApprovals = Object.values(state.signerUi.approvals).flat();
    return allApprovals.find((approval) => approval.id === approvalId) || null;
};
export const selectAllFields = (state: RootState): FieldView[] => {
    const {fields} = state.signerUi;
    return Object.values(fields ?? {}).flat();
};

export const selectFieldById = (fieldId: string | null | undefined) => (state: RootState): FieldView | null => {
    if (!fieldId) return null;
    const allFields = selectAllFields(state);
    return allFields.find((field) => field.id === fieldId) || null;
};

export const selectFieldValidationError = (fieldId: string | null | undefined) => (state: RootState): string | null => {
    if (!fieldId) return null;
    return state.signerUi.fieldValidationErrors[fieldId] ?? null;
};

export const selectIsApprovalOrFieldSaving = (id: string | null | undefined) => (state: RootState): boolean => {
    if (!id) return false;
    return state.signerUi.savingApprovalsAndFieldsIds.includes(id);
};

export const selectIsConfirmDocumentLoading = (state: RootState): boolean => state.signerUi.isConfirmDocumentLoading;
export const selectIsUndoApprovalLoading = (state: RootState): boolean => state.signerUi.isUndoApprovalLoading;
export const selectIsUploadAttachmentLoading = (state: RootState): boolean => state.signerUi.isUploadAttachmentLoading;
export const selectSignature = (state: RootState): SignatureResource | null => state.signerUi.signature;
export const selectIsInitialLoadInProgress = (state: RootState): boolean => state.signerUi.isInitialLoadInProgress;
export const selectRecipient = (state: RootState): RecipientView | null => state.signerUi.recipient;
export const selectRecipientId = (state: RootState): string | null => state.signerUi.recipient?.id || null;
export const selectRecipientLanguage = (state: RootState): Locale => {
    const {recipient} = state.signerUi;
    const language = recipient?.language || DEFAULT_LOCALE;
    return language;
};
export const selectAttachments = (state: RootState): AttachmentView[] => state.signerUi.attachments || [];
export const selectAttachmentById = (attachmentId: string | null | undefined) => (state: RootState): AttachmentView | null => {
    if (!attachmentId) return null;
    return state.signerUi.attachments?.find((att) => att.id === attachmentId) || null;
};

export const selectAttachmentUploads = (state: RootState): Record<string, AttachmentUploadView[]> => state.signerUi.attachmentUploads;

export const selectAttachmentUploadsByAttachmentId = (attachmentId: string | null | undefined) => (state: RootState): AttachmentUploadView[] => {
    if (!attachmentId) return [];
    return state.signerUi.attachmentUploads[attachmentId] || [];
};

export const selectIsAttachmentUploadsLoading = (state: RootState): boolean => state.signerUi.isAttachmentUploadsLoading;
export const selectConfigurations = (state: RootState): Record<string, ConfigurationResource> => state.signerUi.configurations;
export const selectConfigurationByType = (configurationType: ConfigurationType | null | undefined) => (state: RootState): ConfigurationResource | null => {
    if (!configurationType) return null;
    return state.signerUi.configurations[configurationType] || null;
};
export const selectMergedTransactionSettings = createSelector(
    [
        (state: RootState) => selectConfigurationByType('transaction_settings')(state),
        selectTransaction
    ],
    (configurationSettings, transaction): Record<string, any> => ({
        ...configurationSettings,
        ...transaction?.settings
    })
);

export const selectIsChangeRecipientLoading = (state: RootState): boolean => state.signerUi.isChangeRecipientLoading;
export const selectChangeRecipientError = (state: RootState): ChangeRecipientError | null => state.signerUi.changeRecipientError;
export const selectIsLanguageUpdateLoading = (state: RootState): boolean => state.signerUi.isLanguageUpdateLoading;
export const selectIsCreateDeclineReasonLoading = (state: RootState): boolean => state.signerUi.isCreateDeclineReasonLoading;
export const selectAllRecipients = (state: RootState): RecipientView[] => state.signerUi.allRecipients;
export const selectIsAllRecipientsLoading = (state: RootState): boolean => state.signerUi.isAllRecipientsLoading;
export const selectVerification = (state: RootState): VerificationView | null => state.signerUi.verification;
export const selectIsVerificationLoading = (state: RootState): boolean => state.signerUi.isVerificationLoading;
export const selectAccountProvider = (state: RootState): AccountProviderView | null => state.signerUi.accountProvider;
export const selectIsAccountProviderLoading = (state: RootState): boolean => state.signerUi.isAccountProviderLoading;
export const selectAccountFeatures = (state: RootState): unknown => state.signerUi.accountFeatures;
export const selectIsAccountFeaturesLoading = (state: RootState): boolean => state.signerUi.isAccountFeaturesLoading;
export const selectNextSigning = (state: RootState): NextSigning | null => state.signerUi.nextSigning;
export const selectIsNextSigningLoading = (state: RootState): boolean => state.signerUi.isNextSigningLoading;
export const selectNextSigningError = (state: RootState): string | null => state.signerUi.nextSigningError;

export const selectRadioGroupState = (groupName: string | undefined, approvalId: string | undefined) => (state: RootState): RadioGroupState => {
    if (!groupName || !approvalId) {
        return {
            selectedFieldId: null,
            fields: []
        };
    }

    const fields = state.signerUi.fields[approvalId] || [];
    const groupFields = fields.filter((f) => f.group === groupName && f.type?.toLowerCase() === FIELD_TYPES.RADIO);
    const selectedField = groupFields.find((f) => f.value === 'true');

    return {
        selectedFieldId: selectedField?.id || null,
        fields: groupFields
    };
};

export const {
    resetSignerUiState,
    setIsInitialLoadInProgress,
    setTransactionId,
    setUrlRecipientId,
    setActiveAccountId,
    setSelectedSigner,
    setIsLeftMenuOpen,
    setIsDocumentsMenuOpen,
    setIsAttachmentsMenuOpen,
    setSelectedAttachmentId,
    clearSignature,
    setIsDeclinePanelOpen,
    setIsReassignPaneOpen,
    setIsInterfaceHidden,
    setAuthObject,
    setFieldsConditions,
    setIsHardwareTokenDialogOpen,
    setCachedHanddrawnSignature,
    setCachedImageSignature,
    setCachedSignatureData,
    clearCachedSignatureData,
    setDelegateSignature,
    clearDelegateSignature,
    setAnimatedSignatureId,
    setIsDelegatedTransaction,
    setDefaultBrand,
    openNextPendingUpload,
    resetNextSigning,
    setIsApprovalOrFieldSaving,
    setApprovalsLoadedForTransaction,
    setFieldsLoadedForTransaction,
    setFieldValue,
    setFieldValidationError,
    clearFieldValidationError
} = signerUiSlice.actions;

export default signerUiSlice.reducer;

export type {
    SignerUiState,
    RootState,
    Locale,
    FieldCondition,
    CachedSignatureData,
    DelegateSignature,
    JWTPayload,
    UserFetchResult,
    ApiErrorPayload,
    FetchApprovalsResult,
    FetchDocumentFieldsResult,
    FetchAttachmentUploadsResult,
    FetchUserArgs,
    FetchTransactionArgs,
    FetchApprovalsArgs,
    FetchDocumentApprovalsArgs,
    FetchDocumentFieldsArgs,
    FetchAttachmentUploadsArgs,
    FillFieldArgs,
    SignApprovalArgs,
    UpdateRadioGroupSelectionArgs,
    UpdateRadioGroupSelectionResult,
    ConfirmDocumentArgs,
    UndoApprovalArgs,
    UploadAttachmentFileArgs,
    FetchConfigurationArgs,
    FetchConfigurationResult,
    RadioGroupState,
    ChangeRecipientArgs,
    ChangeRecipientError,
    CreateDeclineReasonArgs,
    CreateDeclineReasonResult
} from './signerUiSlice.types';
